import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Image gallery — Strapi sections.life-image-gallery. Header + a responsive
// image grid. Used by careers, key-integrations, life-at-cymmetri. The legacy
// version was a carousel; this is a clean CSS grid (no carousel dep).
export const lifeGallerySchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  images: z.array(z.string()).default([]),
});

type LifeGalleryProps = z.infer<typeof lifeGallerySchema>;

const Section = styled.section`
  max-width: 120rem;
  margin: 0 auto;
  padding: 6rem 1.6rem;

  @media (min-width: 64em) {
    padding: 8rem 2.4rem;
  }
`;

const Header = styled.div`
  max-width: 72rem;
  margin: 0 auto 4rem;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
`;

const Heading = styled.h2`
  margin: 0.8rem 0 0;
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.8rem, 4vw, 4.8rem);
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  line-height: 1.7;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.6rem;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 64em) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Img = styled.img`
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 1.6rem;
  background: #f4f7ff;
`;

function LifeGallery({ eyebrow, heading, description, images }: LifeGalleryProps) {
  return (
    <Section>
      <Header>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Header>
      {images.length > 0 ? (
        <Grid>
          {images.map((src, i) => (
            <Img key={`${src}-${i}`} src={src} alt="" loading="lazy" />
          ))}
        </Grid>
      ) : null}
    </Section>
  );
}

export const lifeGalleryBlock: BlockDefinition<LifeGalleryProps> = {
  type: 'lifeGallery',
  schema: lifeGallerySchema,
  Renderer: LifeGallery,
};
